<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.entry_sheet')"
    icon="mdi-package-down"
    color="primary"
    :save-text="$t('add')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
    :hide-footer="editedItem?.id"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print"></print-button>
    </template>

    <v-form ref="form" v-model="valid" v-if="editedItem">
      <v-row>
        <v-col cols="12" sm="12" md="4" lg="4" xl="2">
          <choose-warehouse-field
            v-model="editedItem.warehouse"
            return-object
            hide-details
            color="white"
            :disabled="editedItem?.id"
          ></choose-warehouse-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
            :disabled="editedItem?.id"
          ></text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
            :readonly="editedItem?.id"
          ></date-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.ordered_by"
            :items="users"
            return-object
            :loading="loadingUsers"
            :disabled="editedItem?.id"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.ordered_by')"
            required
            color="white"
          ></autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.received_by"
            :items="users"
            return-object
            :loading="loadingUsers"
            :disabled="editedItem?.id"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.received_by')"
            required
            color="white"
          ></autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" xl="2">
          <choose-suplier-field
            hide-details
            v-model="editedItem.supplier"
            color="white"
            :disabled="editedItem?.id"
          ></choose-suplier-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.invoice_number')"
            v-model="editedItem.document_number"
            hide-details
            color="white"
            :disabled="editedItem?.id"
          ></text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2">
          <date-picker
            :label="$t('warehouse.shipping_date')"
            v-model="editedItem.document_date"
            single-date
            color="white"
            :disabled="editedItem?.id"
          ></date-picker>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" xl="2">
          <text-area
            color="white"
            :label="$t('note')"
            :disabled="editedItem?.id"
            v-model="editedItem.notes"
            rows="1"
          ></text-area>
        </v-col>
      </v-row>
    </v-form>

    <warehouse-purchase-order-table
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      entry
      @removeGrocery="removeGrocery"
      v-if="selectedWarehouse"
      :disabled="editedItem?.id"
    >
    </warehouse-purchase-order-table>
    <div
      class="text-center grey--text text--darken-1 caption mt-2"
      v-if="selectedWarehouse"
    >
      <v-icon small color="grey darken-1">mdi-keyboard-return</v-icon>
      {{ $t("warehouse.quick_add_hint") }}
    </div>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import ChooseWarehouseField from "@/components/warehouse/field/ChooseWarehouseField.vue";
import WarehousePurchaseOrderTable from "@/components/warehouse/requirement/purchase/WarehousePurchaseOrderTable.vue";
import { waybillDocumentGrocery } from "@/models/warehouse";
import ChooseSuplierField from "@/components/warehouse/field/ChooseSuplierField.vue";
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "WarehouseDialogOrder",
  mixins: [hasModel, hasPrinting, hasSnackbar, hasSelectedWarehouse],
  components: {
    ChooseWarehouseField,
    WarehousePurchaseOrderTable,
    ChooseSuplierField,
    FullscreenDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: { ...waybillDocumentGrocery },
    },
  },
  data() {
    return {
      search: "",
      valid: false,
      loadingUsers: false,
      loading: false,
    };
  },
  methods: {
    removeGrocery(grocery) {
      this.editedItem.groceries = this.editedItem.groceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    print() {
      const headers = this.headers.filter((header) => header.value !== "actions");
      const items = this.editedItem.groceries.map((item, index) => ({
        ...item,
        num: index + 1,
      }));
      this.printDocument({
        tables: [{ headers, items }],
        title: this.$t("delivery_note_no") + " " + this.editedItem.number,
        beforeTitleHtml: `

          <h2 style="margin-bottom: 0px">${this.$t("stock")}: ${this.toLatin(
          this.editedItem.warehouse?.name || ""
        )}</h2>
            <div style="font-size: 16px;margin-top: 20px;">
              ${this.$t("date")}: ${this.localeDate(this.editedItem.document_date)}
            </div>
          `,
        signature: {
          right: {
            title: this.$t("warehouse.received_by"),
            name: this.toLatin(
              [
                this.editedItem.received_by?.name,
                this.editedItem.received_by?.lastname,
              ].join(" ") || ""
            ),
          },
          left: {
            title: this.$t("warehouse.delivered_by"),
            name: this.toLatin(
              [
                this.editedItem.dispatched_by?.name,
                this.editedItem.dispatched_by?.lastname,
              ].join(" ") || ""
            ),
          },
        },
        hideDatetime: true,
      });
    },
    async save() {
      const params = {
        ...this.editedItem,
        document_type: "entry",
        transaction_type: "entry",
        supplier: this.editedItem.supplier?.id || null, // Ensure supplier ID is passed or null
        ordered_by: this.editedItem.ordered_by.id,
        received_by: this.editedItem.received_by.id,
        groceries: this.editedItem.groceries.map((grocery) => ({
          ...grocery,
          warehouse_id: this.editedItem.warehouse.id,
          grocery_id: grocery.id,
          transaction_type: "entry",
        })),
        warehouse_id: this.editedItem.warehouse.id,
      };
      const request = warehouseService.addDocumentTransaction(params);
      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.$t("warehouse_entry_success"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false; // Close dialog on success
        this.$emit("success"); // Emit success event
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false; // Reset loading state
      }
    },
    async fetchUsers(refetch = false) {
      try {
        this.loadingUsers = true;
        await this.$store.dispatch("restaurantStore/dispatchUsers", { refetch });
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
  },
  computed: {
    users() {
      return this.$store.state.restaurantStore.users.map((user) => ({
        ...user,
        fullName: [user.name, user.lastname].map((name) => this.toLatin(name)).join(" "),
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "150px",
        },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("quantity"), value: "quantity" },
        { text: this.$t("warehouse.warehouse_status"), value: "warehouse_stock" },
        { text: "", value: "actions", width: "100px" },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.fetchUsers();
      }
    },
  },
};
</script>
